import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import OrderDetails from "../OrderDetails/OrderDetails";
import { Accordion, Button, Form } from "react-bootstrap";
import down from "../../../Assets/Icons/down.png";
import { MapPin, ChevronDown, Pencil, Trash2 } from "lucide-react";
import { useRestaurantOrderDetails } from "./useRestaurantOrderDetails";
import axios from "axios";
import "./RestaurantOrderDetails.css";

const RestaurantOrderDetails = ({
  restaurant,
  restaurantIndex,
  handleSetTip,
  tips,
  slectedTableHistroyForValidation,
  setIsToggleOn,
  isToggleOn,
  DeliverySelfCollectionStateInParent,
  isServiceChargeEnabled,
  handleServiceChargeToggle,
  hasDelivery,
  address,
  selectedAddressId,
  handleAddressSelect,
  handleShowAddressModal,
  changeDeliveryAddress,
  onUpdateDeliveryNote,
  handleCloseUpdateModal,
  handleDeleteAddress,
  handleUpdateDefaultAddress,
  showAddressError,
  deliveryFee,
  deliveryDistance,
  deliveryDuration,
  onSubOrderTypeChange,
}) => {
  const {
    restaurantId,
    setRestaurantId,
    selectedTable,
    setSelectedTable,
    disabledAmount,
    disabledPercentage,
    specify,
    setSpecify,
    isAccordionExpanded,
    setIsAccordionExpanded,
    totalBasePrice2,
    handleSpecifyInputValue,
    handleSpecifyPercentageInput,
    DeliverySelfCollectionState,
    handleSetTableDetails,
  } = useRestaurantOrderDetails({
    restaurant,
    handleSetTip,
    tips,
    slectedTableHistroyForValidation,
    setIsToggleOn,
    isToggleOn,
    DeliverySelfCollectionStateInParent,
    isServiceChargeEnabled,
    handleServiceChargeToggle,
  });

  console.log("deliveryFee", deliveryFee);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [originalDeliveryNote, setOriginalDeliveryNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [isSelfCollectionActive, setIsSelfCollectionActive] = useState(false);
  const [savedDeliveryData, setSavedDeliveryData] = useState({}); // { subOrderId: { addressDetails, deliveryNote } }
  const [fallbackAddresses, setFallbackAddresses] = useState({}); // { temp_id: addressObj }

  const dropdownRef = useRef(null);

  // Sync local self-collection state with actual data to prevent UI desync
  useEffect(() => {
    const hasDeliveryItems = restaurant.subOrders.some((subOrder) => {
      if (subOrder.orderTypeName === "Delivery") return true;
      return subOrder.itemDetails.some(
        (item) =>
          (item.effectiveOrderType ?? subOrder.orderTypeName) === "Delivery",
      );
    });

    if (hasDeliveryItems && isSelfCollectionActive) {
      setIsSelfCollectionActive(false);
    }
  }, [restaurant.subOrders, isSelfCollectionActive, setIsSelfCollectionActive]);

  // Close dropdown on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsAddressDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      if (isAddressDropdownOpen) {
        setIsAddressDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isAddressDropdownOpen]);

  // FETCH saved delivery data per subOrder
  useEffect(() => {
    if (!restaurant?.subOrders?.length) return;

    const fetchSavedDelivery = async () => {
      const savedMap = {};
      const fallbackMap = {};

      for (const subOrder of restaurant.subOrders) {
        if (subOrder.orderTypeName !== "Delivery") continue;

        try {
          const res = await axios.get(
            `${process.env.REACT_APP_SECRET}/api/Cart/GetSavedDeliveryAddress?subOrderId=${subOrder.subOrderId}`,
          );

          const { deliveryAddressDetails = "", deliveryNote = "" } =
            res.data || {};

          if (deliveryAddressDetails || deliveryNote) {
            // Try to match address string to known addresses
            const match = address.find(
              (addr) => addr.address.trim() === deliveryAddressDetails.trim(),
            );

            if (match) {
              savedMap[subOrder.subOrderId] = {
                addressId: match.id,
                deliveryNote,
              };
            }
          }
        } catch (err) {
          console.warn(`No saved delivery for subOrder ${subOrder.subOrderId}`);
        }
      }

      setSavedDeliveryData(savedMap);
      setFallbackAddresses(fallbackMap);
    };

    fetchSavedDelivery();
  }, [restaurant?.subOrders, address]);

  // Compute effective delivery address & note (per first delivery subOrder)
  const deliverySubOrders = restaurant.subOrders.filter(
    (so) => so.orderTypeName === "Delivery",
  );
  const firstDeliverySubOrder = deliverySubOrders[0];
  const saved = firstDeliverySubOrder
    ? savedDeliveryData[firstDeliverySubOrder.subOrderId] || {}
    : {};

  // Merge real + fallback addresses
  const allAddresses = [...address, ...Object.values(fallbackAddresses)];
  const effectiveSelectedAddressId =
    selectedAddressId || saved.addressId || null;

  const effectiveSelectedAddress = allAddresses.find(
    (a) => a.id === effectiveSelectedAddressId,
  );

  // Sync delivery note & address on change
  useEffect(() => {
    const noteToUse = deliveryNote || saved.deliveryNote || "";
    if (noteToUse !== deliveryNote) {
      setDeliveryNote(noteToUse);
      setOriginalDeliveryNote(noteToUse);
    }
  }, [saved.deliveryNote, deliveryNote]);

  useEffect(() => {
    if (
      effectiveSelectedAddressId &&
      effectiveSelectedAddressId !== selectedAddressId
    ) {
      // Notify parent only if it's a real address (not fallback)
      const isFallback = effectiveSelectedAddressId.startsWith("temp_");
      if (!isFallback) {
        handleAddressSelect(effectiveSelectedAddressId);
      }
    }
  }, [effectiveSelectedAddressId, selectedAddressId, handleAddressSelect]);

  // Compute effective delivery visibility
  const hasAnyDeliveryItems = restaurant.subOrders.some((subOrder) => {
    return subOrder.itemDetails.some((item) => {
      const effectiveType = item.effectiveOrderType ?? subOrder.orderTypeName;
      return effectiveType === "Delivery";
    });
  });

  const effectiveHasDelivery = hasAnyDeliveryItems && !isSelfCollectionActive;

  // Track the last subOrderId we want to update
  const [lastUpdatedSubOrderId, setLastUpdatedSubOrderId] = useState(null);

  // Effect to call API ONLY when deliveryDistance/duration change AND we have a valid subOrderId
  useEffect(() => {
    // Only trigger if we have a valid firstDeliverySubOrder and new distance/duration
    if (
      firstDeliverySubOrder &&
      typeof deliveryDistance === "number" &&
      typeof deliveryDuration === "number" &&
      lastUpdatedSubOrderId === firstDeliverySubOrder.subOrderId
    ) {
      console.log(
        "✅ Calling UpdateDeliveryAddressV2 with fresh values:",
        "SubOrderId:",
        firstDeliverySubOrder.subOrderId,
        "Distance:",
        deliveryDistance,
        "Duration:",
        deliveryDuration,
      );

      const addrId = effectiveSelectedAddressId;
      if (!addrId) {
        console.warn("No address selected, skipping API call");
        return;
      }

      const addr = allAddresses.find((a) => a.id === addrId);
      if (!addr) {
        console.warn("Address not found, skipping API call");
        return;
      }

      // Call the API with the latest values
      changeDeliveryAddress(
        addr,
        deliveryNote.trim(),
        firstDeliverySubOrder.subOrderId,
        deliveryDistance,
        deliveryDuration,
      )
        .then(() => {
          console.log("✅ API call succeeded");
        })
        .catch((error) => {
          console.error("❌ API call failed:", error);
          toast.error("Failed to save delivery address");
        });
    }
  }, [
    deliveryDistance,
    deliveryDuration,
    firstDeliverySubOrder?.subOrderId,
    effectiveSelectedAddressId,
    deliveryNote,
    changeDeliveryAddress,
    allAddresses,
    lastUpdatedSubOrderId,
  ]);

  return (
    <div ref={dropdownRef} className="restaurant-order-details-wrapper">
      <Accordion
        className="my-3 parent-body-shadow"
        id={`restaurant-table-section-${restaurant?.cartRestaurantId}`}
        defaultActiveKey={isAccordionExpanded ? "1" : null}
      >
        <Accordion.Item eventKey="1">
          <Accordion.Header
            className="parent-header-shadow"
            onClick={() => {
              setRestaurantId(restaurant?.cartRestaurantId);
              setIsAccordionExpanded(!isAccordionExpanded);
            }}
          >
            <div className="d-flex align-items-center justify-content-between w-100">
              <div className="d-flex align-items-center">
                <div className="header-weight-bold">
                  {restaurant?.restaurantName}
                </div>
                <div className="ms-2">
                  <img
                    src={down}
                    alt="arrow"
                    style={{
                      width: "16px",
                      height: "16px",
                      transform: isAccordionExpanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
              </div>
              <div className="header-weight-bold">
                £{totalBasePrice2().toFixed(2)}
              </div>
            </div>
          </Accordion.Header>

          <Accordion.Body className="order-details-container">
            {(() => {
              // Step 1: Group all items by effective type
              const itemsByType = {};
              restaurant.subOrders.forEach((subOrder) => {
                subOrder.itemDetails.forEach((item) => {
                  const effectiveType =
                    item.effectiveOrderType ?? subOrder.orderTypeName;
                  if (!itemsByType[effectiveType]) {
                    itemsByType[effectiveType] = [];
                  }
                  itemsByType[effectiveType].push(item);
                });
              });

              // Step 2: Define FIXED order of accordions
              const accordionOrder = ["Delivery", "Collection", "In-Store"];

              return accordionOrder
                .filter((type) => {
                  // Only show "Delivery" or "Collection" if there are relevant items
                  if (type === "Delivery" || type === "Collection") {
                    return (itemsByType[type]?.length ?? 0) > 0;
                  }
                  // Always show "In-Store" if it exists in original data
                  return restaurant.subOrders.some(
                    (so) => so.orderTypeName === "In-Store",
                  );
                })
                .map((type) => (
                  <OrderDetails
                    key={`${restaurant.cartRestaurantId}-${type}`}
                    title={type}
                    restaurant={restaurant}
                    restaurantIndex={restaurantIndex}
                    selectedTable={selectedTable}
                    setSelectedTable={setSelectedTable}
                    selectedOkTable={slectedTableHistroyForValidation}
                    setIsToggleOn={setIsToggleOn}
                    isToggleOn={isToggleOn}
                    DeliverySelfCollectionState={DeliverySelfCollectionState}
                    isServiceChargeEnabled={isServiceChargeEnabled}
                    handleServiceChargeToggle={() =>
                      handleServiceChargeToggle(restaurant?.cartRestaurantId)
                    }
                    onSelfCollectionToggle={(isActive) =>
                      setIsSelfCollectionActive(isActive)
                    }
                    deliveryFee={deliveryFee}
                    onSubOrderTypeChange={onSubOrderTypeChange}
                  />
                ));
            })()}

            {/* ➕ Address Selection Dropdown */}
            {effectiveHasDelivery && (
              <div className="address-selection-container">
                <div className="d-flex align-items-center mb-2">
                  <MapPin className="me-2" size={18} color="#6c757d" />
                  <span className="fw-bold text-muted">Deliver to:</span>
                </div>

                <div className="address-selector-wrapper">
                  {/* Dropdown Trigger */}
                  <div
                    className={`address-selector-field d-flex align-items-center ${
                      effectiveSelectedAddressId ? "has-value" : ""
                    }`}
                    onClick={() =>
                      setIsAddressDropdownOpen(!isAddressDropdownOpen)
                    }
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsAddressDropdownOpen(!isAddressDropdownOpen);
                      }
                    }}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={isAddressDropdownOpen}
                  >
                    {effectiveSelectedAddress ? (
                      <>
                        <span className="address-text">
                          {effectiveSelectedAddress.address.split("\n")[0]}
                        </span>
                        {effectiveSelectedAddress.tags && (
                          <span className="address-tag ms-2">
                            {effectiveSelectedAddress.tags}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="placeholder-text">
                        Select an address
                      </span>
                    )}
                    <ChevronDown
                      className="ms-auto"
                      size={16}
                      color="#6c757d"
                      style={{
                        flexShrink: 0,
                        transform: isAddressDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </div>

                  {/* Plus Button (Add new address) */}
                  <button
                    className="btn btn-outline-secondary ms-2 plus-button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowAddressModal();
                    }}
                    aria-label="Add new address"
                  >
                    +
                  </button>
                </div>

                {/* <h5 className="mt-2">
                  SubOrder ID: {firstDeliverySubOrder?.subOrderId ?? "N/A"}
                </h5>
                <h5>{deliveryDistance} miles</h5>
                <h5>{deliveryDuration} minutes</h5>
                <h5>{deliveryFee} euro</h5> */}

                {/* Dropdown Menu */}
                {isAddressDropdownOpen && (
                  <div className="address-dropdown-menu" role="listbox">
                    {allAddresses.length > 0 ? (
                      allAddresses.map((addr) => (
                        <div
                          key={addr.id}
                          className={`address-dropdown-item ${
                            effectiveSelectedAddressId === addr.id
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddressSelect(addr.id);
                            // 👇 SET the subOrderId we're about to update
                            setLastUpdatedSubOrderId(
                              firstDeliverySubOrder?.subOrderId,
                            );
                            setIsAddressDropdownOpen(false);
                          }}
                          role="option"
                          aria-selected={effectiveSelectedAddressId === addr.id}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleAddressSelect(addr.id);
                              // 👇 SET the subOrderId we're about to update
                              setLastUpdatedSubOrderId(
                                firstDeliverySubOrder?.subOrderId,
                              );
                              setIsAddressDropdownOpen(false);
                            }
                          }}
                        >
                          <div className="address-line">
                            <strong>{addr.address.split("\n")[0]}</strong>
                          </div>
                          {addr.address.includes("\n") && (
                            <div className="address-line text-muted small">
                              {addr.address.split("\n").slice(1).join(", ")}
                            </div>
                          )}
                          {addr.tags && (
                            <div className="address-line">
                              <span className="address-tag">{addr.tags}</span>
                            </div>
                          )}

                          {/* Edit & Delete only for real addresses */}
                          {!addr.id.startsWith("temp_") && (
                            <div className="address-action-buttons">
                              <button
                                className="address-edit-button"
                                type="button"
                                aria-label={`Edit address: ${
                                  addr.address.split("\n")[0]
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCloseUpdateModal(addr);
                                }}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="address-delete-button"
                                type="button"
                                aria-label={`Delete address: ${
                                  addr.address.split("\n")[0]
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAddress(addr.id);
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="address-dropdown-item text-muted">
                        No saved addresses.
                      </div>
                    )}

                    <div
                      className="address-dropdown-item add-new"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddressDropdownOpen(false);
                        handleShowAddressModal();
                      }}
                      role="option"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setIsAddressDropdownOpen(false);
                          handleShowAddressModal();
                        }
                      }}
                    >
                      ➕ Add new address…
                    </div>
                  </div>
                )}

                {/* Delivery Note — BEM */}
                <div className="delivery-note">
                  <button
                    type="button"
                    className="delivery-note__toggle"
                    onClick={() => setShowNoteInput(!showNoteInput)}
                    aria-expanded={showNoteInput}
                    aria-controls="delivery-note-input"
                  >
                    + Add delivery note for rider
                  </button>

                  {showNoteInput && (
                    <div className="delivery-note__input-wrapper">
                      <Form.Control
                        as="textarea"
                        id="delivery-note-input"
                        rows={2}
                        placeholder="Enter instructions for the rider..."
                        value={deliveryNote}
                        onChange={(e) => setDeliveryNote(e.target.value)}
                        className="delivery-note__input mb-2"
                        aria-label="Delivery instructions for rider"
                      />

                      {deliveryNote.trim() !== originalDeliveryNote && (
                        <div className="d-flex justify-content-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const addrId = effectiveSelectedAddressId;
                              if (!addrId) {
                                toast.warn("Please select an address first");
                                return;
                              }

                              const firstDeliverySubOrder =
                                deliverySubOrders[0];
                              if (!firstDeliverySubOrder) {
                                toast.warn("No delivery sub-order found");
                                return;
                              }

                              onUpdateDeliveryNote(
                                addrId,
                                deliveryNote.trim(),
                                firstDeliverySubOrder.subOrderId,
                              );

                              const isFallback = addrId.startsWith("temp_");
                              if (isFallback) {
                                setFallbackAddresses((prev) => ({
                                  ...prev,
                                  [addrId]: {
                                    ...prev[addrId],
                                    notes: deliveryNote.trim(),
                                  },
                                }));
                              }

                              setOriginalDeliveryNote(deliveryNote.trim());
                            }}
                            className="delivery-note__save-btn"
                          >
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tips Section */}
            <div className="tips-window">
              <h5 style={{ fontWeight: "bold", marginBottom: "7px" }}>
                Tip Your Restaurant
              </h5>
              <div className="tips-button-group">
                <button
                  onClick={() =>
                    handleSetTip(restaurant?.cartRestaurantId, 0, "amount")
                  }
                  className={`price-button controlled-font-size ${
                    tips === 0 ? "price-button-active" : ""
                  }`}
                >
                  Not Now
                </button>
                <button
                  onClick={() =>
                    handleSetTip(restaurant?.cartRestaurantId, 10, "amount")
                  }
                  className={`price-button ${
                    tips === 10 ? "price-button-active" : ""
                  }`}
                >
                  £10
                </button>
                <button
                  onClick={() =>
                    handleSetTip(restaurant?.cartRestaurantId, 20, "amount")
                  }
                  className={`price-button ${
                    tips === 20 ? "price-button-active" : ""
                  }`}
                >
                  £20
                </button>
                <button
                  onClick={() =>
                    handleSetTip(restaurant?.cartRestaurantId, 30, "amount")
                  }
                  className={`price-button ${
                    tips === 30 ? "price-button-active" : ""
                  }`}
                >
                  £30
                </button>
                <button
                  onClick={() =>
                    handleSetTip(restaurant?.cartRestaurantId, 50, "amount")
                  }
                  className={`price-button ${
                    tips === 50 ? "price-button-active" : ""
                  }`}
                >
                  £50
                </button>
              </div>
              <div className="specify-group">
                <button
                  onClick={() => setSpecify(!specify)}
                  className={`price-button ${
                    specify ? "price-button-active me-2" : ""
                  }`}
                >
                  Specify
                </button>
                {specify && (
                  <>
                    <div className="w-100">
                      <input
                        className={`specify-input ${
                          specify ? "specify-input-active" : ""
                        }`}
                        placeholder="Enter an amount"
                        type="number"
                        min="0"
                        step="0.01"
                        onKeyDown={(e) => {
                          if (
                            e.ctrlKey ||
                            e.metaKey ||
                            e.altKey ||
                            [
                              "Backspace",
                              "Delete",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "Home",
                              "End",
                            ].includes(e.key)
                          ) {
                            return;
                          }

                          const { value, selectionStart, selectionEnd } =
                            e.target;
                          const char = e.key;

                          if (!/^\d|\.$/.test(char)) return;

                          const prefix = value.slice(0, selectionStart);
                          const suffix = value.slice(selectionEnd);
                          const newValue = prefix + char + suffix;

                          const [int, dec] = newValue.split(".");
                          if (
                            (char === "." && value.includes(".")) ||
                            (dec && dec.length > 1)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) =>
                          handleSpecifyInputValue(
                            e,
                            restaurant?.cartRestaurantId,
                          )
                        }
                        disabled={disabledAmount}
                      />
                    </div>
                    <div className="fw-bold ms-4">Or</div>
                    <div className="w-100">
                      <input
                        className={`specify-input ${
                          specify ? "specify-input-active" : ""
                        }`}
                        placeholder="Enter a percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        onInput={(e) => {
                          let val = e.target.value;
                          val = val.replace(/\D/g, "");
                          let num = val === "" ? 0 : Number(val);
                          num = Math.min(100, Math.max(0, num));
                          e.target.value = num;
                        }}
                        onChange={(e) => {
                          handleSpecifyPercentageInput(
                            e,
                            restaurant?.cartRestaurantId,
                          );
                        }}
                        disabled={disabledPercentage}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default RestaurantOrderDetails;
